import React, { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { BottomNav, TopBar } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const IncentiveCalculator = ({ user }) => {
  const [netSales, setNetSales] = useState('');
  const [role, setRole] = useState('Rep');
  const [quarter, setQuarter] = useState('Q1-2026');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateIncentive = async () => {
    if (!netSales || parseFloat(netSales) <= 0) {
      toast.error('Please enter a valid net sales amount');
      return;
    }

    setLoading(true);
    try {
      const response = await api.calculateIncentive({
        net_sales: parseFloat(netSales),
        role,
        quarter,
      });
      setResult(response.data);
    } catch (error) {
      toast.error('Failed to calculate incentive');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    return `₹${amount.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-nav">
      <TopBar title="Incentive Calculator" subtitle="Calculate your earnings" user={user} />

      <div className="p-4 md:p-8 space-y-6">
        {/* Calculator Form */}
        <Card data-testid="incentive-calculator-card" className="p-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="net-sales">Net Sales (₹)</Label>
              <Input
                id="net-sales"
                data-testid="net-sales-input"
                type="number"
                value={netSales}
                onChange={(e) => setNetSales(e.target.value)}
                placeholder="5000000"
              />
            </div>

            <div>
              <Label htmlFor="role">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="role-select-incentive">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Rep">Rep</SelectItem>
                  <SelectItem value="Manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="quarter">Quarter</Label>
              <Select value={quarter} onValueChange={setQuarter}>
                <SelectTrigger data-testid="quarter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Q1-2026">Q1 2026</SelectItem>
                  <SelectItem value="Q2-2026">Q2 2026</SelectItem>
                  <SelectItem value="Q3-2026">Q3 2026</SelectItem>
                  <SelectItem value="Q4-2026">Q4 2026</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              data-testid="calculate-incentive-button"
              onClick={calculateIncentive}
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Calculating...' : 'Calculate Incentive'}
            </Button>
          </div>
        </Card>

        {/* Result */}
        {result && (
          <>
            <Card data-testid="payout-result-card" className="p-6 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white shadow-lg">
              <div className="text-center">
                <p className="text-sm opacity-90 mb-2">Your Estimated Payout</p>
                <p className="text-5xl font-barlow font-bold tabular-nums">
                  {formatCurrency(result.payout)}
                </p>
              </div>
            </Card>

            {/* Current Slab */}
            {result.current_slab && (
              <Card data-testid="current-slab-card" className="p-6">
                <h3 className="text-lg font-barlow font-bold uppercase mb-4">Current Slab</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Min Sales:</span>
                    <span className="font-bold">{formatCurrency(result.current_slab.min_sales)}</span>
                  </div>
                  {result.current_slab.max_sales && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Max Sales:</span>
                      <span className="font-bold">{formatCurrency(result.current_slab.max_sales)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-600">Payout:</span>
                    <span className="font-bold text-emerald-600">
                      {result.current_slab.fixed_amount !== null && result.current_slab.fixed_amount !== undefined
                        ? `₹${result.current_slab.fixed_amount.toLocaleString()}`
                        : `${result.current_slab.payout_percentage}%`
                      }
                      {result.current_slab.per_unit && ` per ${formatCurrency(result.current_slab.per_unit)}`}
                    </span>
                  </div>
                </div>
              </Card>
            )}

            {/* Next Slab */}
            {result.next_slab && result.delta > 0 && (
              <Card data-testid="next-slab-card" className="p-6 bg-blue-50 border-blue-200">
                <div className="flex items-start gap-3">
                  <TrendingUp size={20} className="text-blue-600 mt-1" />
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-blue-900 mb-2">Next Milestone</h3>
                    <p className="text-xs text-blue-700 mb-3">
                      Sell {formatCurrency(result.delta)} more to reach the next slab!
                    </p>
                    <div className="bg-blue-100 rounded-lg p-3 text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-blue-700">Next Slab Min:</span>
                        <span className="font-bold text-blue-900">{formatCurrency(result.next_slab.min_sales)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Payout:</span>
                        <span className="font-bold text-emerald-600">
                          {result.next_slab.fixed_amount !== null && result.next_slab.fixed_amount !== undefined
                            ? `₹${result.next_slab.fixed_amount.toLocaleString()}`
                            : `${result.next_slab.payout_percentage}%`
                          }
                          {result.next_slab.per_unit && ` per ${formatCurrency(result.next_slab.per_unit)}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </>
        )}
      </div>

      <BottomNav user={user} />
    </div>
  );
};

export default IncentiveCalculator;
